/**
 * Server-side paged work-order reads for the service module.
 *
 * Why this exists: `GET /serviceModule/work-orders` used to answer the store's
 * WHOLE list in one body, so a workshop with thousands of imported work orders
 * downloaded, normalised, persisted and re-filtered all of them on every list
 * visit. This helper asks for one page at a time (`page` / `limit` / `search` /
 * `status`) and for the per-status counters (`summary=true`) instead.
 *
 * Deploy-skew safe: an older backend ignores `envelope` / `page` / `limit` /
 * `summary` and answers a bare ARRAY. That is detected (an array where an
 * envelope was requested) and the helper falls back to reading the legacy list
 * once, then filters / sorts / slices it on the device exactly like the app did
 * before — nothing breaks, it is merely as slow as it used to be.
 */
import {apiFetch, normalizeWorkOrder} from './WorkSheetContext';

/** Default page size of the work-order list screen. @type {number} */
export const WORK_ORDER_PAGE_SIZE = 30;

/** Size of the "working set" the dashboard / calendar read from the context. @type {number} */
export const WORK_ORDER_WORKING_SET_LIMIT = 300;

const WORK_ORDERS_PATH = '/serviceModule/work-orders';

/** Backend statuses that never show up in the app (mirrors WorkSheetContext's terminal set). */
const TERMINAL_STATUS_TOKENS = new Set(['cancelled', 'canceled', 'cancel', 'rejected', 'archived']);

/**
 * Backend (raw) statuses behind each list filter pill. Mirrors STATUS_MAP in
 * WorkSheetContext (raw status -> pending / in_progress / completed / invoiced);
 * `quote` has no pill of its own and is only part of "active" / "all".
 * @type {Record<string, string[]>}
 */
const FILTER_GROUPS = {
    pending: ['open', 'pending', 'scheduled', 'awaitingParts'],
    in_progress: ['inProgress', 'in_progress', 'inprogress', 'awaitingNorm', 'workInProgress', 'onHold'],
    completed: ['completed', 'done', 'finished', 'awaitingInvoice'],
    invoiced: ['invoiced', 'closed'],
};

const ACTIVE_EXTRA = ['quote'];

/**
 * Raw backend statuses for a list filter (`active`, `pending`, `in_progress`,
 * `completed`, `invoiced`, `all`). `active` = everything not finished/closed,
 * `all` = everything except cancelled-type records.
 *
 * @param {string} filter List filter key.
 * @returns {string[]} Raw status tokens to send as the comma `status` filter.
 */
export const statusesForFilter = (filter) => {
    if (filter === 'active') {
        return [...ACTIVE_EXTRA, ...FILTER_GROUPS.pending, ...FILTER_GROUPS.in_progress];
    }

    if (filter === 'all') {
        return [...ACTIVE_EXTRA, ...FILTER_GROUPS.pending, ...FILTER_GROUPS.in_progress, ...FILTER_GROUPS.completed, ...FILTER_GROUPS.invoiced];
    }

    return FILTER_GROUPS[filter] ? [...FILTER_GROUPS[filter]] : [];
};

const buildQuery = (params) => Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

const isTerminal = (workOrder) => {
    const token = (value) => String(value || '').toLowerCase().replace(/[^a-z]/g, '');

    return TERMINAL_STATUS_TOKENS.has(token(workOrder?.status)) || TERMINAL_STATUS_TOKENS.has(token(workOrder?.rawStatus));
};

const CLOSED_FRONTEND_STATUSES = new Set(['completed', 'invoiced']);

/**
 * Whether a NORMALISED work order belongs under a list filter.
 *
 * @param {object} workOrder Normalised work order (frontend status).
 * @param {string} filter List filter key.
 * @returns {boolean} True when it should be listed.
 */
export const matchesFilter = (workOrder, filter) => {
    if (filter === 'all') {
        return true;
    }

    if (filter === 'active') {
        return !CLOSED_FRONTEND_STATUSES.has(workOrder?.status);
    }

    return workOrder?.status === filter;
};

/**
 * Case-insensitive match of the list search box against a normalised work order.
 *
 * @param {object} workOrder Normalised work order.
 * @param {string} term Raw search text.
 * @returns {boolean} True when the term is empty or any searchable field contains it.
 */
export const matchesSearch = (workOrder, term) => {
    const q = String(term || '').trim().toLowerCase();

    if (!q) {
        return true;
    }

    return [workOrder?.customer, workOrder?.plate, workOrder?.worksheetId, workOrder?.car]
        .some((field) => String(field || '').toLowerCase().includes(q));
};

const createdTime = (workOrder) => {
    const time = Date.parse(workOrder?.createdAt);

    return Number.isNaN(time) ? 0 : time;
};

/** Newest-first comparator on `createdAt`. */
export const byNewestFirst = (a, b) => createdTime(b) - createdTime(a);

/**
 * Pull the row array out of any list body the endpoint may answer
 * (bare array, `{docs}`, `{data}`, `{workOrders}`, `{worksheets}`).
 *
 * @param {*} body Parsed response.
 * @returns {object[]} The rows (raw backend shape).
 */
export const extractRows = (body) => {
    if (Array.isArray(body)) {
        return body;
    }

    return body?.docs || body?.data || body?.workOrders || body?.worksheets || [];
};

let legacyBackendUntil = 0;
const LEGACY_REMEMBER_MS = 60 * 1000;

const readLegacyList = async (options) => {
    const body = await apiFetch(WORK_ORDERS_PATH, options);

    return extractRows(body).map(normalizeWorkOrder).filter((wo) => !isTerminal(wo));
};

/**
 * Read ONE page of work orders.
 *
 * @param {object} [params]
 * @param {number} [params.page=1] 1-based page.
 * @param {number} [params.limit=WORK_ORDER_PAGE_SIZE] Page size.
 * @param {string} [params.filter='active'] Status pill key.
 * @param {string} [params.search=''] Free-text search (server-side where supported).
 * @param {boolean} [params.skipCache=false] Bypass the short GET cache (pull-to-refresh).
 * @returns {Promise<{items: object[], total: number, page: number, pages: number, legacy: boolean}>}
 *   Normalised, newest-first rows plus paging info. `legacy` is true when the
 *   backend predates paging and the page was cut on the device.
 */
export const fetchWorkOrderPage = async ({page = 1, limit = WORK_ORDER_PAGE_SIZE, filter = 'active', search = '', skipCache = false} = {}) => {
    const options = skipCache ? {skipCache: true} : {};
    const term = String(search || '').trim();

    const sliceLegacy = async () => {
        legacyBackendUntil = Date.now() + LEGACY_REMEMBER_MS;
        const all = (await readLegacyList(options))
            .filter((wo) => matchesFilter(wo, filter) && matchesSearch(wo, term))
            .sort(byNewestFirst);
        const start = (page - 1) * limit;

        return {
            items: all.slice(start, start + limit),
            total: all.length,
            page,
            pages: Math.max(1, Math.ceil(all.length / limit)),
            legacy: true,
        };
    };

    if (Date.now() < legacyBackendUntil) {
        return sliceLegacy();
    }

    const query = buildQuery({
        page,
        limit,
        envelope: 'true',
        status: statusesForFilter(filter).join(','),
        search: term || undefined,
    });
    const body = await apiFetch(`${WORK_ORDERS_PATH}?${query}`, options);

    if (Array.isArray(body)) {
        // An envelope was requested; a bare array means the backend ignored the
        // paging params (and treated the comma status as one unknown value).
        return sliceLegacy();
    }

    const rows = extractRows(body).map(normalizeWorkOrder).filter((wo) => !isTerminal(wo));
    const total = Number(body?.total);
    const pages = Number(body?.pages);

    return {
        items: rows,
        total: Number.isFinite(total) ? total : rows.length,
        page: Number(body?.page) || page,
        pages: Number.isFinite(pages) && pages > 0 ? pages : (rows.length >= limit ? page + 1 : page),
        legacy: false,
    };
};

/**
 * Count normalised work orders per list pill (used for legacy bodies).
 *
 * @param {object[]} list Normalised work orders.
 * @returns {{all: number, active: number, pending: number, in_progress: number, completed: number, invoiced: number}}
 */
export const countByFilter = (list = []) => {
    const counts = {all: 0, active: 0, pending: 0, in_progress: 0, completed: 0, invoiced: 0};

    list.forEach((wo) => {
        counts.all += 1;

        if (!CLOSED_FRONTEND_STATUSES.has(wo?.status)) {
            counts.active += 1;
        }

        if (counts[wo?.status] !== undefined && wo.status !== 'all' && wo.status !== 'active') {
            counts[wo.status] += 1;
        }
    });

    return counts;
};

/**
 * Turn a `{byStatus: {rawStatus: n}}` summary into the per-pill counters.
 *
 * @param {Record<string, number>} byStatus Raw-status -> count map.
 * @returns {{all: number, active: number, pending: number, in_progress: number, completed: number, invoiced: number}}
 */
export const countsFromByStatus = (byStatus = {}) => {
    const sum = (statuses) => statuses.reduce((total, status) => total + (Number(byStatus[status]) || 0), 0);

    return {
        all: sum(statusesForFilter('all')),
        active: sum(statusesForFilter('active')),
        pending: sum(FILTER_GROUPS.pending),
        in_progress: sum(FILTER_GROUPS.in_progress),
        completed: sum(FILTER_GROUPS.completed),
        invoiced: sum(FILTER_GROUPS.invoiced),
    };
};

/**
 * Per-pill work-order counters from the cheap `summary=true` aggregate.
 *
 * @param {object} [options]
 * @param {boolean} [options.skipCache=false] Bypass the short GET cache.
 * @returns {Promise<{all: number, active: number, pending: number, in_progress: number, completed: number, invoiced: number}>}
 */
export const fetchWorkOrderCounts = async ({skipCache = false} = {}) => {
    const options = skipCache ? {skipCache: true} : {};

    if (Date.now() >= legacyBackendUntil) {
        const body = await apiFetch(`${WORK_ORDERS_PATH}?summary=true`, options);

        if (body && !Array.isArray(body) && body.byStatus && typeof body.byStatus === 'object') {
            return countsFromByStatus(body.byStatus);
        }

        legacyBackendUntil = Date.now() + LEGACY_REMEMBER_MS;
    }

    return countByFilter(await readLegacyList(options));
};

/** Test hook: forget the remembered "backend predates paging" verdict. */
export const resetLegacyBackendMemory = () => {
    legacyBackendUntil = 0;
};
